<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps({
  channel: {
    type: String,
    required: true
  },
  country: {
    type: String,
    required: true
  }
});

const { t } = useI18n();

const channelType = computed(() => {
  if (props.channel === 'viber-chatbots') return 'bots';
  if (props.channel === 'viber-business-calls') return 'bc';
  if (props.channel === 'viber-business-messages') return 'bm';
  return 'bm';
});

const translatedChannel = computed(() => {
  return t(`howToStart.channels.${channelType.value}`);
});
</script>

<template>
  <div class="how-to-start page-section bg-gradient-to-b from-white to-gray-50 py-16">
    <div class="container mx-auto px-4 max-w-5xl">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-extrabold text-gray-900 flex items-center justify-center gap-3">
          <span class="text-4xl">🚀</span>
          {{ $t('howToStart.title', { channel: translatedChannel, country: country }) }}
        </h2>
      </div>

      <div class="relative">
        <!-- Connecting Line -->
        <div class="absolute hidden md:block w-full h-1 bg-gradient-to-r from-blue-100 via-blue-300 to-blue-100 top-1/2 -translate-y-1/2 rounded-full opacity-50 z-0"></div>

        <div class="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-4 relative z-10">
          <!-- Step 1 -->
          <div class="step-card group">
            <div class="step-icon-wrapper">
              <div class="step-number">1</div>
              <div class="step-icon">
                <i class="pi pi-file-edit"></i>
              </div>
            </div>
            <div class="step-content">
              <h3>{{ $t('howToStart.steps.step1') }}</h3>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="step-card group">
            <div class="step-icon-wrapper">
              <div class="step-number">2</div>
              <div class="step-icon">
                <i class="pi pi-credit-card"></i>
              </div>
            </div>
            <div class="step-content">
              <h3>{{ $t('howToStart.steps.step2') }}</h3>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="step-card group">
            <div class="step-icon-wrapper">
              <div class="step-number">3</div>
              <div class="step-icon">
                <i class="pi pi-user-plus"></i>
              </div>
            </div>
            <div class="step-content">
              <h3>{{ $t('howToStart.steps.step3') }}</h3>
            </div>
          </div>

          <!-- Step 4 -->
          <div class="step-card group">
            <div class="step-icon-wrapper">
              <div class="step-number">4</div>
              <div class="step-icon">
                <i class="pi pi-check-circle"></i>
              </div>
            </div>
            <div class="step-content">
              <h3>{{ $t(`howToStart.steps.${channelType}.step4`) }}</h3>
            </div>
          </div>

          <!-- Step 5 -->
          <div class="step-card group">
            <div class="step-icon-wrapper">
              <div class="step-number">5</div>
              <div class="step-icon">
                <i class="pi pi-send"></i>
              </div>
            </div>
            <div class="step-content">
              <h3>{{ $t(`howToStart.steps.${channelType}.step5`) }}</h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.step-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    
    .step-icon {
      background: linear-gradient(135deg, #3070b6, #044068);
      color: white;
      box-shadow: 0 10px 25px rgba(48, 112, 182, 0.4);
    }
    .step-number {
      background: #7c3aed;
      color: white;
      border-color: #7c3aed;
    }
  }
}

.step-icon-wrapper {
  position: relative;
  margin-bottom: 1.5rem;
}

.step-icon {
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: #3070b6;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 2px solid #eff6ff;
  
  i {
    transition: transform 0.3s ease;
  }
}

.step-number {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 28px;
  height: 28px;
  background: white;
  border: 2px solid #e5e7eb;
  color: #6b7280;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  z-index: 2;
  transition: all 0.3s ease;
}

.step-content {
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.4;
    padding: 0 0.5rem;
  }
}

@media (max-width: 767px) {
  .step-card {
    flex-direction: row;
    text-align: left;
    background: white;
    padding: 1.25rem;
    border-radius: 1rem;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    border: 1px solid #f3f4f6;
  }

  .step-icon-wrapper {
    margin-bottom: 0;
    margin-right: 1.5rem;
  }

  .step-icon {
    width: 60px;
    height: 60px;
    font-size: 1.25rem;
  }
}
</style>
